import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable.jsx";
import SearchFilter from "../../Requirement/components/common/SearchFilter.jsx";
import { fetchData } from "../../Requirement/services/RequirementServices";
import "../../../styles/buyer.css";
import OfferDetails from "../requirement/component/OfferDetails";
const CompareOffer = () => {
  const [offers, setOffers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [rowStart, setRowStart] = useState(0);
  const pageSize = 10;
  const { setLoading } = useLoading();
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedOffers, setSelectedOffers] = useState([]); // For multiple selection
  const [compareMode, setCompareMode] = useState(false);
  const [activeTab, setActiveTab] = useState("basicInfo"); // Tab state for offer details

  // Dropdown data
  const [products, setProducts] = useState([]);
  const [countries, setCountries] = useState([]);

  const [searchValues, setSearchValues] = useState({
    ProductName: "",
    Grade: "",
    DestinationCountry: "",
    OriginCountry: "",
    OfferType: "",
  });

  // Fetch products from backend
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listProducts",
        rowStart: 0,
        pageSize: 1000,
        filterTerms: {},
      });

      setProducts(response.items || response || []);
    } catch (error) {
      console.error("Error fetching products", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Fetch countries from backend
  const fetchCountries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData({
        target: "listDropDownOptions",
        filterTerms: {
          Type: "Country",
        },
      });

      setCountries(response.items || response || []);
    } catch (error) {
      console.error("Error fetching countries", error);
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  // Fetch offers from backend
  const fetchOffers = useCallback(
    async (filterValues = searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const response = await fetchData({
          target: "listBuyerItemDetails",
          filterTerms: {
            product: filterValues.ProductName || undefined,
            grade: filterValues.Grade || undefined,
            DestinationCountry: filterValues.DestinationCountry || undefined,
            OriginCountry: filterValues.OriginCountry || undefined,
            OfferType: filterValues.OfferType || undefined,
          },
          userOrder: "CreatedAt DESC",
          rowStart: newRowStart,
          pageSize: pageSize,
        });

        setOffers(response.items || response || []);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error fetching offers: ", error);
        setOffers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [searchValues, rowStart, setLoading, pageSize]
  );

  // Handle page change
  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    fetchOffers(searchValues, newRowStart);
  };

  // Handle search
  const handleSearch = (searchValues) => {
    setSearchValues(searchValues);
    setRowStart(0);
    fetchOffers(searchValues, 0);
  };

  // Handle row click
  const handleRowClick = (offer) => {
    console.log(offer);
    if (compareMode) {
      // In compare mode, toggle selection
      setSelectedOffers((prev) => {
        const isSelected = prev.find((o) => o.GPOfferID === offer.GPOfferID);
        if (isSelected) {
          return prev.filter((o) => o.GPOfferID !== offer.GPOfferID);
        } else if (prev.length < 4) {
          // Limit to 4 offers for comparison
          return [...prev, offer];
        }
        return prev;
      });
    } else {
      // Normal mode, select single offer
      setSelectedOffer(offer);
    }
  };
  // Toggle compare mode
  const toggleCompareMode = () => {
    setCompareMode((prev) => !prev);
    if (!compareMode) {
      setSelectedOffer(null);
      setSelectedOffers([]);
    }
  };

  // Clear selections
  const clearSelections = () => {
    setSelectedOffer(null);
    setSelectedOffers([]);
  };

  // Format price to 2 decimal places
  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  // Format date to DD/MM/YYYY format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
      const year = date.getFullYear();

      return `${day}/${month}/${year}`; // Returns DD/MM/YYYY format
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };

  // Handle tab click for offer details
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Define table columns
  const columns = [
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "FOBSalePrice",
      numeric: true,
      label: "FOB Sale Price",
      renderCell: (row) => row.FOBSalePrice,
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    { id: "OfferType", numeric: false, label: "Offer Type" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
  ];

  // Define search filter fields
  const filterFields = [
    {
      name: "ProductName",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select Product" },
        ...[...new Set(products.map((product) => product.ProductName))].map(
          (product) => ({
            value: product,
            label: product,
          })
        ),
      ],
      value: searchValues.ProductName,
      onChange: (value) => {
        setSearchValues((prev) => ({
          ...prev,
          ProductName: value,
          Grade: "", // Reset grade when product changes
        }));
      },
    },
    {
      name: "Grade",
      label: "Grade",
      type: "dropdown",
      options: [
        { value: "", label: "Select Grade" },
        ...products
          .filter((product) => product.ProductName === searchValues.ProductName)
          .map((product) => ({
            value: product.Grade,
            label: product.Grade,
          })),
      ],
      value: searchValues.Grade,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, Grade: value })),
      disabled: !searchValues.ProductName,
    },
    {
      name: "OriginCountry",
      label: "Origin Country",
      type: "dropdown",
      options: [
        { value: "", label: "Select Origin Country" },
        ...countries.map((country) => ({
          value: country.Value,
          label: country.Value,
        })),
      ],
      value: searchValues.OriginCountry,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, OriginCountry: value })),
    },
    {
      name: "DestinationCountry",
      label: "Destination Country",
      type: "dropdown",
      options: [
        { value: "", label: "Select Destination Country" },
        ...countries.map((country) => ({
          value: country.Value,
          label: country.Value,
        })),
      ],
      value: searchValues.DestinationCountry,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, DestinationCountry: value })),
    },
    {
      name: "OfferType",
      label: "Offer Type",
      type: "dropdown",
      options: [
        { value: "", label: "All Types" },
        { value: "Individual", label: "Individual" },
        { value: "Manual", label: "Manual" },
        { value: "Bulk", label: "Bulk" },
      ],
      value: searchValues.OfferType,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, OfferType: value })),
    },
  ];

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  // Load initial data
  useEffect(() => {
    fetchProducts();
    fetchCountries();
  }, [fetchProducts, fetchCountries]);

  // Load offers when dependencies change
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  return (
    <div className="compare-offer-container">
      {/* <div className="compare-offer-header">
                <div className="header-content">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">Compare Offers</h2>
                        <p className="mt-2 text-gray-600">
                            Browse and compare available product offers from global partners.
                        </p>
                    </div>
                   
                </div>
                {compareMode && (
                    <div className="compare-info">
                        <p className="text-sm text-blue-600">
                            Compare Mode: Click on offers to select them for comparison (max 4 offers). 
                            Selected: {selectedOffers.length}/4
                        </p>
                    </div>
                )}
            </div> */}
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Compare Offers</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={filterFields} onSearch={handleSearch} />
        </div>
      </div>
      {/* Search Filters */}
      {/* <div className="search-filter-section mb-4">
                <SearchFilter fields={filterFields} onSearch={handleSearch} />
            </div> */}

      {/* Offers Table */}
      <div className="offers-table-section">
        <DataTable
          columns={columns}
          data={offers}
          total={totalItems}
          title={compareMode ? "Select Offers to Compare" : "Available Offers"}
          onRowClick={handleRowClick}
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          selectedRows={
            compareMode
              ? selectedOffers.map((o) => o.GPOfferID)
              : selectedOffer
              ? [selectedOffer.GPOfferID]
              : []
          }
        />
        <div className="header-actions">
          <button
            className={`submit-btn-more ${compareMode ? "active" : ""}`}
            onClick={toggleCompareMode}
          >
            {compareMode ? "Exit Compare Offer" : "Compare Offer"}
          </button>
          {(selectedOffer || selectedOffers.length > 0) && (
            <button className="submit-btn-more" onClick={clearSelections}>
              Clear Selection
            </button>
          )}
        </div>
      </div>

      {/* Comparison Table for Multiple Offers */}
      {compareMode && selectedOffers.length > 1 && (
        <div className="comparison-table-section mb-6">
          <h3 className="text-lg font-semibold mb-4">Offer Comparison</h3>
          <div className="comparison-table-container">
            <table className="comparison-table datatable">
              <thead>
                <tr
                  style={{
                    backgroundColor: "#de6b14",
                    height: "30px",
                    color: "#ffffff",
                  }}
                  className="dataheader"
                >
                  <th
                    rowSpan="2"
                    style={{
                      verticalAlign: "middle",
                      backgroundColor: "#de6b14",
                      color: "#ffffff",
                      fontWeight: "bold",
                    }}
                  >
                    Basic Info
                  </th>
                  {selectedOffers.map((offer, index) => (
                    <th
                      key={offer.GPOfferID}
                      style={{
                        backgroundColor: "#de6b14",
                        color: "#ffffff",
                        position: "relative",
                      }}
                    >
                      Offer: {offer.OfferSequenceID}
                      <button
                        className="remove-offer-btn"
                        onClick={() =>
                          setSelectedOffers((prev) =>
                            prev.filter((o) => o.GPOfferID !== offer.GPOfferID)
                          )
                        }
                        style={{
                          position: "absolute",
                          top: "5px",
                          right: "5px",
                          background: "white",
                          color: "#ff4444",
                          border: "none",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        ×
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                  >
                    Category
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.Category || ""}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Product Name
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.ProductName}</td>
                  ))}
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Product Grade
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.Grade}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Originate Country
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.Country}</td>
                  ))}
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Specification
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.productSpecifications}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Offer Quantity(MT)
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{formatPrice(offer.Quantity)}</td>
                  ))}
                </tr>
                <tr style={{ backgroundColor: "#de6b14", color: "#ffffff" }}>
                  <td style={{ color: "#fff", fontWeight: "bold" }}>Pricing</td>
                  <td
                    colSpan={selectedOffers.length}
                    style={{ color: "#fff" }}
                  ></td>
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Currency
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>USD</td>
                  ))}
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    FOB Sale Price
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID} style={{ textAlign: "right" }}>
                      {offer.FOBSalePrice}
                    </td>
                  ))}
                </tr>

                <tr style={{ backgroundColor: "#de6b14", color: "#ffffff" }}>
                  <td style={{ color: "#fff", fontWeight: "bold" }}>
                    Shipping
                  </td>
                  <td
                    colSpan={selectedOffers.length}
                    style={{ color: "#155724" }}
                  ></td>
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Loading Country
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.Country}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Loading Port
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>
                      {offer.LoadingPort || "Haldia"}
                    </td>
                  ))}
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Destination Country
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.DestinationCountry}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Destination Port
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{offer.DischargePort || ""}</td>
                  ))}
                </tr>
                <tr style={{ backgroundColor: "#de6b14", color: "#ffffff" }}>
                  <td style={{ color: "#fff", fontWeight: "bold" }}>Date</td>
                  <td
                    colSpan={selectedOffers.length}
                    style={{ color: "#880e4f" }}
                  ></td>
                </tr>
                <tr className="primerow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Offer Date
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{formatDate(offer.CreatedAt)}</td>
                  ))}
                </tr>
                <tr className="altrow">
                  <td
                    style={{ backgroundColor: "#f8f9fa", fontWeight: "normal" }}
                  >
                    Offer Expire Date
                  </td>
                  {selectedOffers.map((offer) => (
                    <td key={offer.GPOfferID}>{formatDate(offer.Expires)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Selected Offer Details - Tabbed Interface */}

      {!compareMode && selectedOffer && (
        <OfferDetails
          offer={selectedOffer}
          btnShortlist={false}
          btnSendQuery={false}
          btnRequestQuote={false}
          btnRequestSpecifications={false}
          btnRequestBestPrice={false}
          btnNegotiate={false}
          btnRequestShippingDetails={false}
          btnLogisticsSupport={false}
          btnRaiseQuery={true}
        />
      )}
    </div>
  );
};

export default CompareOffer;
